/**
 * InvoicesList Component
 *
 * Paginated invoices list with status/month/contractor filters,
 * colored status badges, and generate invoice button for admin.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ContractorInvoice, InvoiceStatus, ContractorProfile } from '@/types/contractors';
import { INVOICE_STATUS_CONFIG } from '@/types/contractors';
import { contractorInvoicesApi, contractorsApi } from '@/services/contractorsApi';

const PAGE_SIZE = 20;

interface InvoicesListProps {
  isAdmin?: boolean;
}

export function InvoicesList({ isAdmin = false }: InvoicesListProps): React.JSX.Element {
  const { t } = useTranslation('contractors');
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<ContractorInvoice[]>([]);
  const [contractors, setContractors] = useState<ContractorProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const [billingMonth, setBillingMonth] = useState('');
  const [contractorFilter, setContractorFilter] = useState<number | ''>('');

  // Fetch contractors for admin filter
  useEffect(() => {
    if (!isAdmin) return;
    contractorsApi.list({ limit: 200 }).then((res) => {
      setContractors(Array.isArray(res) ? res : []);
    }).catch(() => { /* ignore */ });
  }, [isAdmin]);

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (statusFilter) params.status = statusFilter;
      if (billingMonth) params.billing_month = billingMonth;
      if (contractorFilter) params.contractor_user_id = contractorFilter;

      const result = await contractorInvoicesApi.list(params as Parameters<typeof contractorInvoicesApi.list>[0]);
      setInvoices(Array.isArray(result) ? result : []);
      setTotal(Array.isArray(result) ? result.length : 0);
    } catch {
      setError(t('errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter, billingMonth, contractorFilter, t]);

  useEffect(() => {
    void fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, billingMonth, contractorFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{t('invoices.title')}</h2>
        {isAdmin && (
          <button
            onClick={() => navigate('/contractors/invoices/generate')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('invoices.generate')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('invoices.status')}</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | '')}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('common.all')}</option>
            <option value="draft">{t('status.draft')}</option>
            <option value="pending">{t('status.pending')}</option>
            <option value="paid">{t('status.paid')}</option>
            <option value="overdue">{t('status.overdue')}</option>
            <option value="cancelled">{t('status.cancelled')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('invoices.billingMonth')}</label>
          <input
            type="month"
            value={billingMonth}
            onChange={(e) => setBillingMonth(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {isAdmin && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('invoices.contractor')}</label>
            <select
              value={contractorFilter}
              onChange={(e) => setContractorFilter(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.all')}</option>
              {contractors.map((c) => (
                <option key={c.user_id} value={c.user_id}>{c.display_name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.number')}</th>
              {isAdmin && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.contractor')}</th>}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.billingMonth')}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('invoices.net')}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('invoices.vat')}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('invoices.gross')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.status')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('invoices.dueDate')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={isAdmin ? 8 : 7} className="px-4 py-8 text-center text-sm text-gray-500">{t('common.loading')}</td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 8 : 7} className="px-4 py-8 text-center text-sm text-gray-500">{t('invoices.noInvoices')}</td>
              </tr>
            ) : (
              invoices.map((inv) => {
                const cfg = INVOICE_STATUS_CONFIG[inv.status];
                return (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/contractors/invoices/${inv.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-blue-600">{inv.invoice_number}</td>
                    {isAdmin && <td className="px-4 py-3 text-sm text-gray-700">{inv.contractor_name || '-'}</td>}
                    <td className="px-4 py-3 text-sm text-gray-700">{inv.billing_month}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">{formatCurrency(inv.amount_net, inv.currency)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">{formatCurrency(inv.vat_amount, inv.currency)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(inv.amount_gross, inv.currency)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${cfg.bgColor} ${cfg.color}`}>
                        {t(`status.${inv.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{inv.due_date}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {t('common.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              {t('common.previous')}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
